import {
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChildren,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TitleBarComponent } from '../../shared/components/title-bar/title-bar.component';
import { EmptyResultComponent } from '../../shared/components/empty-result/empty-result.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { ApiService } from '../../services/api.service';

interface TenancyReconcileTable {
  table?: string;
  nullCount?: number;
  mismatchCount?: number;
  error?: string;
}

interface TenancyReconcilePayload {
  clean?: boolean;
  checkedAt?: string;
  tables?: TenancyReconcileTable[];
}

interface TenancyBackfillPayload {
  updatedTotal?: number;
  tables?: TenancyReconcileTable[];
  reconcile?: TenancyReconcilePayload;
}

interface ReconcileIssue {
  table: string;
  nullCount: number;
  mismatchCount: number;
  error?: string;
}

interface JsonLine {
  content: string;
  issueIndex: number | null;
}

@Component({
  selector: 'app-admin-tenancy-reconcile',
  standalone: true,
  imports: [CommonModule, TitleBarComponent, EmptyResultComponent, LoadingTableShimmerComponent],
  templateUrl: './admin-tenancy-reconcile.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./admin-tenancy-reconcile.component.scss'],
})
export class AdminTenancyReconcileComponent implements OnInit {
  private readonly api = inject(ApiService);

  @ViewChildren('jsonIssue') private readonly issueElements!: QueryList<ElementRef<HTMLElement>>;

  readonly shimmerColumns: ShimmerColumn[] = [
    { label: 'Table', width: 'lg' },
    { label: 'Nulls', width: 'sm' },
    { label: 'Mismatches', width: 'sm' },
    { label: 'Status', width: 'md' },
  ];

  loading = true;
  backfilling = false;
  errorMessage = '';
  jsonLines: JsonLine[] = [];
  issues: ReconcileIssue[] = [];
  activeIssueIndex = 0;
  isClean: boolean | null = null;
  checkedAt: string | null = null;
  backfillMessage = '';

  get issueSummary(): string {
    const nullCount = this.issues.reduce((total, issue) => total + issue.nullCount, 0);
    const mismatchCount = this.issues.reduce((total, issue) => total + issue.mismatchCount, 0);
    const errors = this.issues.filter(issue => issue.error).length;
    const parts = [
      `${this.issues.length} affected table${this.issues.length === 1 ? '' : 's'}`,
      `${nullCount} missing company ID${nullCount === 1 ? '' : 's'}`,
      `${mismatchCount} mismatched company ID${mismatchCount === 1 ? '' : 's'}`,
    ];

    if (errors > 0) {
      parts.push(`${errors} query error${errors === 1 ? '' : 's'}`);
    }

    return parts.join(' · ');
  }

  get showEmptyState(): boolean {
    return !this.loading && !!this.errorMessage && this.jsonLines.length === 0;
  }

  get showPayloadEmpty(): boolean {
    return (
      !this.loading && !this.errorMessage && this.isClean === null && this.jsonLines.length === 0
    );
  }

  ngOnInit(): void {
    this.load();
  }

  refresh(): void {
    this.load();
  }

  previousIssue(): void {
    this.selectIssue(this.activeIssueIndex - 1);
  }

  nextIssue(): void {
    this.selectIssue(this.activeIssueIndex + 1);
  }

  runBackfill(): void {
    if (this.backfilling || this.loading) {
      return;
    }
    this.backfilling = true;
    this.errorMessage = '';
    this.backfillMessage = '';
    this.api
      .postWithSuperAdminToken<TenancyBackfillPayload>('admin/super-admin/tenancy/backfill', {})
      .subscribe({
        next: response => {
          const payload = (response?.data ?? response) as TenancyBackfillPayload;
          const reconcile = payload?.reconcile;
          this.applyReconcile(reconcile ?? payload);
          const updated = payload?.updatedTotal ?? 0;
          this.backfillMessage = `Backfill updated ${updated} row(s). Reconcile refreshed.`;
          this.backfilling = false;
        },
        error: err => {
          this.errorMessage =
            err?.error?.message || err?.message || 'Failed to run tenancy backfill';
          this.backfilling = false;
        },
      });
  }

  private load(): void {
    this.loading = true;
    this.errorMessage = '';
    this.backfillMessage = '';
    this.api
      .getWithSuperAdminToken<TenancyReconcilePayload>('admin/super-admin/tenancy/reconcile')
      .subscribe({
        next: response => {
          const payload = (response?.data ?? response) as TenancyReconcilePayload;
          this.applyReconcile(payload);
          this.loading = false;
        },
        error: err => {
          this.errorMessage =
            err?.error?.message || err?.message || 'Failed to load tenancy reconcile result';
          this.jsonLines = [];
          this.issues = [];
          this.activeIssueIndex = 0;
          this.isClean = null;
          this.checkedAt = null;
          this.loading = false;
        },
      });
  }

  private applyReconcile(payload: TenancyReconcilePayload | TenancyBackfillPayload | null): void {
    if (!payload) {
      this.jsonLines = [];
      this.issues = [];
      this.activeIssueIndex = 0;
      this.isClean = null;
      this.checkedAt = null;
      return;
    }
    // After backfill, prefer nested reconcile snapshot when present.
    const reconcile =
      'reconcile' in payload && payload.reconcile
        ? payload.reconcile
        : (payload as TenancyReconcilePayload);
    this.issues = (reconcile.tables || [])
      .filter(
        table => (table.nullCount || 0) > 0 || (table.mismatchCount || 0) > 0 || !!table.error
      )
      .map(table => ({
        table: table.table || 'any',
        nullCount: table.nullCount || 0,
        mismatchCount: table.mismatchCount || 0,
        error: table.error,
      }));
    this.activeIssueIndex = 0;
    this.jsonLines = this.createJsonLines(reconcile);
    this.isClean = typeof reconcile.clean === 'boolean' ? reconcile.clean : null;
    this.checkedAt = reconcile.checkedAt || null;
  }

  private selectIssue(index: number): void {
    if (this.issues.length === 0) {
      return;
    }

    this.activeIssueIndex = (index + this.issues.length) % this.issues.length;
    queueMicrotask(() => {
      const issueElement = this.issueElements.find(
        element => Number(element.nativeElement.dataset['issueIndex']) === this.activeIssueIndex
      );
      issueElement?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  private createJsonLines(reconcile: TenancyReconcilePayload): JsonLine[] {
    const lines = JSON.stringify(reconcile, null, 2).split('\n');
    let currentIssueIndex: number | null = null;

    return lines.map(content => {
      const tableMatch = content.match(/"table":\s*"([^"]+)"/);
      if (tableMatch) {
        currentIssueIndex = this.issues.findIndex(issue => issue.table === tableMatch[1]);
        if (currentIssueIndex === -1) {
          currentIssueIndex = null;
        }
      }

      return { content, issueIndex: currentIssueIndex };
    });
  }
}
